import { graphql, useFragment } from "react-relay";
import { CompetitionCardFragment$key } from "./__generated__/CompetitionCardFragment.graphql";
import ActivityCard from "./ActivityCard";
import Reward from "./Reward";
import { Badge } from "@/kit/ui/badge";
import { FormattedMessage, FormattedNumber } from "react-intl";
import EntityProfilePic from "./EntityProfilePic";
import { generatePath } from "react-router";
import { EntityProfilePicFragment$key } from "./__generated__/EntityProfilePicFragment.graphql";
import { CompetitionCardFooterFragment$key } from "./__generated__/CompetitionCardFooterFragment.graphql";

const CompetitionCardFragment = graphql`
  fragment CompetitionCardFragment on Competition {
    id
    slug
    title
    thumbnail
    shortDescription
    membership {
      id
      entity {
        ...EntityProfilePicFragment @arguments(thumbnail: true)
      }
    }
    host {
      ...ActivityCardHostFragment
    }
    ...ActivityCardTagListFragment
    ...CompetitionCardFooterFragment
  }
`;

const CompetitionCardFooterFragment = graphql`
  fragment CompetitionCardFooterFragment on Competition {
    rewards(first: 1) {
      totalCount
      edges {
        node {
          ...RewardFragment
        }
      }
    }
  }
`;

interface Props {
  owner: string;
  competition: CompetitionCardFragment$key;
  vertical?: boolean;
}

export default function CompetitionCard({
  owner,
  vertical,
  competition: competitionFragment,
}: Props) {
  const {
    title,
    shortDescription,
    thumbnail,
    slug,
    host,
    membership,
    ...competition
  } = useFragment(CompetitionCardFragment, competitionFragment);

  const hasJoined = !!membership;

  return (
    <ActivityCard
      vertical={vertical}
      title={title}
      shortDescription={shortDescription}
      thumbnail={thumbnail || undefined}
      url={generatePath("/challenges/:owner/tracks/:slug", { slug, owner })}
      host={host}
      hostLabel={<FormattedMessage defaultMessage="Hosted by:" />}
      overlay={
        hasJoined && <CompetitionCardOverlay entity={membership.entity} />
      }
      footer={<CompetitionCardFooter competition={competition} />}
    />
  );
}

interface CompetitionCardOverlayProps {
  entity: EntityProfilePicFragment$key;
}

function CompetitionCardOverlay(props: CompetitionCardOverlayProps) {
  return (
    <>
      <EntityProfilePic entity={props.entity} size="4" />
      <p className="text-white text-sm font-medium leading-5 line-clamp-3">
        <FormattedMessage defaultMessage="You joined this track" />
      </p>
    </>
  );
}

interface CompetitionCardFooterProps {
  competition: CompetitionCardFooterFragment$key;
}

function CompetitionCardFooter(props: CompetitionCardFooterProps) {
  const {
    rewards: { totalCount, edges: rewards },
  } = useFragment(CompetitionCardFooterFragment, props.competition);

  if (rewards.length === 0 && totalCount < 1) return null;

  return (
    <div className="flex justify-start items-center w-full gap-1 ">
      <Reward reward={rewards[0].node} />
      {totalCount - 1 >= 1 && (
        <Badge size="lg" shape="pill">
          <span className="text-rey-700 text-sm font-medium leading-5">
            <FormattedNumber
              value={totalCount - 1}
              notation="compact"
              signDisplay="always"
            />
          </span>
        </Badge>
      )}
    </div>
  );
}
